import type { UserTier } from '@/types/country';

/**
 * Tier Configuration
 * 
 * Defines limits and features for each subscription tier:
 * - FREE: Limited trial for testing
 * - LITE: Regular freelancer usage
 * - PRO: Professional with unlimited resources
 */

export interface TierLimits {
  clients?: number;
  products?: number;
  suppliers?: number;
  invoicesPerYear?: number;
  expensesPerYear?: number;
  ocrPerYear?: number;
}

export interface TierFeatures {
  basic?: boolean;            // Gestione base
  sdi?: boolean;              // Invio fatture elettroniche SDI
  bulkExport?: boolean;       // Export ZIP spese + ricevute
  reports?: boolean;          // Reports statistiche
  customCategories?: boolean; // Categorie spese personalizzabili
  bilingualInvoices?: boolean;// Fatture IT+EN
  accountantAccess?: boolean; // Supporto commercialista
  customTemplates?: boolean;  // Template modificabili
}

export interface TierConfig {
  name: string;
  price: number;
  limits: TierLimits;
  features: TierFeatures;
}

/**
 * Tier configurations with limits and features
 */
export const TIERS: Record<UserTier, TierConfig> = {
  free: {
    name: 'FREE',
    price: 0,
    limits: {
      clients: 3,
      products: 5,
      suppliers: 5,
      invoicesPerYear: 10,
      expensesPerYear: 30,
      ocrPerYear: 10,
    },
    features: {
      basic: true,
    },
  },

  lite: {
    name: 'LITE',
    price: 4.90,
    limits: {
      clients: 10,
      products: 20,
      suppliers: 10,
      invoicesPerYear: 50,
      expensesPerYear: 100,
      ocrPerYear: 100,
    },
    features: {
      sdi: true,
      bulkExport: true,
    },
  },

  pro: {
    name: 'PRO',
    price: 9.90,
    limits: {
      clients: Infinity,
      products: Infinity,
      suppliers: Infinity,
      invoicesPerYear: Infinity,
      expensesPerYear: Infinity,
      ocrPerYear: Infinity,
    },
    features: {
      sdi: true,
      bulkExport: true,
      reports: true,
      customCategories: true,
      bilingualInvoices: true,
      accountantAccess: true,
      customTemplates: true,
    },
  },
};

/**
 * Get tier configuration
 */
export function getTierConfig(tier: UserTier): TierConfig {
  return TIERS[tier];
}

/**
 * Check if a feature is available for a tier
 */
export function hasFeature(tier: UserTier, feature: keyof TierFeatures): boolean {
  return TIERS[tier].features[feature] ?? false;
}

/**
 * Get limit value for a tier
 */
export function getLimit(tier: UserTier, limit: keyof TierLimits): number {
  return TIERS[tier].limits[limit] ?? 0;
}

/**
 * Check if current usage is within tier limits
 */
export function isWithinLimit(
  tier: UserTier,
  limit: keyof TierLimits,
  currentUsage: number
): boolean {
  const maxLimit = getLimit(tier, limit);
  return currentUsage < maxLimit;
}

/**
 * Check if upgrade is needed
 */
export function needsUpgrade(
  currentTier: UserTier,
  feature: keyof TierFeatures
): boolean {
  return !hasFeature(currentTier, feature);
}

/**
 * Get available tiers for upgrade (excluding accountant)
 */
export function getUpgradeTiers(currentTier: UserTier): UserTier[] {
  const tierOrder: UserTier[] = ['free', 'lite', 'pro'];
  const currentIndex = tierOrder.indexOf(currentTier);
  return tierOrder.slice(currentIndex + 1);
}
